import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import Input from "../../../../components/Form/Input";
import { useAppState } from "../../../../context/StateContext";
import useYupValidation from "../../../../hooks/useYupValidation";
import { Error } from "../../../../models/error";
import { CustomerPortalFooter } from "../../../private/Moving/CustomerPortal/CustomerPortalFooter";
import { validateForm } from "../../SignIn/actions";
import useStyles, {
  ContainerFooter,
  ForgotPasswordContainer,
  ForgotPasswordLink,
  ForgotPasswordText,
  HidePasswordIcon,
  LoginButton,
  LoginForm,
  LoginFormWrapper,
  LoginInfo,
  LoginSubtitle,
  LoginTitle,
  LogoImage,
  PageWrapper,
  ShowPasswordIcon,
} from "./styles";

interface LoginFormProps {
  errorReturned?: Error;
  isLoading?: boolean;
  handleSubmit?: (event: React.FormEvent) => void;
}

export interface LoginFormHandler {
  email: string;
  password: string;
}

const FormLogin: ForwardRefRenderFunction<LoginFormHandler, LoginFormProps> = (props, ref) => {
  const { errorReturned, isLoading, handleSubmit } = props;

  const { subDomainInfo } = useAppState();

  const classes = useStyles();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const { t: translate } = useTranslation();

  const history = useHistory();

  useImperativeHandle(ref, () => ({
    email,
    password,
  }));

  const hasErrorReturned = () => {
    if (errorReturned) return true;
  };

  const getErrorReturned = () => {
    if (errorReturned) return translate("oopsIncorrectCredentials");
  };

  const handleSubmitForm = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);
    if (validateForm(translate, { email, password }, setErrors)) {
      if (handleSubmit) handleSubmit(event);
    }
  };

  useEffect(() => {
    if (isSubmitted) validateForm(translate, { email, password }, setErrors);
  }, [isSubmitted, email, password]);

  return (
    <PageWrapper>
      <LoginForm onSubmit={handleSubmitForm}>
        <LogoImage src={subDomainInfo?.logo} />

        <LoginInfo>
          <LoginTitle>{translate("login")}</LoginTitle>
          <LoginSubtitle>{translate("checkYourMoveFileOnline")}</LoginSubtitle>
        </LoginInfo>

        <LoginFormWrapper>
          <Input
            fullWidth
            inputProps={{
              "aria-label": "user-mail",
              autocomplete: "username",
            }}
            label={translate("email")}
            labelClassName={classes.label}
            value={email}
            placeholder={translate("typeHere")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
            error={fieldHasError("email") || hasErrorReturned()}
            errorMessage={getErrorByField("email") || getErrorReturned()}
          />
          <Input
            fullWidth
            inputProps={{
              "aria-label": "user-pwd",
              autoComplete: "off",
            }}
            type={showPassword ? "text" : "password"}
            label={translate("password")}
            labelClassName={classes.label}
            rightIcon={
              showPassword ? (
                <ShowPasswordIcon size={22} onClick={() => setShowPassword(false)} />
              ) : (
                <HidePasswordIcon size={22} onClick={() => setShowPassword(true)} />
              )
            }
            value={password}
            placeholder={translate("typeHere")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(event.target.value)
            }
            error={fieldHasError("password") || hasErrorReturned()}
            errorMessage={getErrorByField("password") || getErrorReturned()}
          />
        </LoginFormWrapper>

        <LoginFormWrapper>
          <LoginButton
            type="submit"
            fullWidth
            loading={isLoading}
            data-cy="loginButton"
            aria-label="loginButton"
          >
            {translate("login")}
          </LoginButton>

          <ForgotPasswordContainer>
            <ForgotPasswordLink onClick={() => history.push("/forgot")}>
              {translate("forgotYourPassword")}
            </ForgotPasswordLink>
            <ForgotPasswordText>{translate("letsGetYouBackIn")}</ForgotPasswordText>
          </ForgotPasswordContainer>
        </LoginFormWrapper>
      </LoginForm>

      <ContainerFooter>
        <CustomerPortalFooter center />
      </ContainerFooter>
    </PageWrapper>
  );
};

export default forwardRef(FormLogin);
